/// <amd-module name="Feed/CreateFeed/Redux/CreateFeed/CreateFeedSaga" />
import { SagaIterator } from "redux-saga";
import { effects } from "redux/redux-saga.compat";
import { ActionWithPayload } from "redux/Utils/ActionHelper";

import { announce } from "VSS/Core/Util/Accessibility";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { Identity } from "VSS/Platform/Contracts/Identities";

import { PerfScenarios } from "Feed/Common/Constants/Constants";
import { Feed, FeedPermission, FeedRole, FeedView, FeedVisibility, UpstreamSource } from "Feed/Common/Contracts/Feed";
import * as Resources from "Feed/Common/Resources";
import { IFeedWebPageDataService } from "Feed/Common/Services/IFeedWebPageDataService";
import { FeedHubActions } from "Feed/Core/Redux/FeedHub/FeedHubActions";
import { PerfTrackerActions } from "Feed/Core/Redux/PerfTracker/PerfTrackerActions";
import { constructNewFeed } from "Feed/CreateFeed/Helpers/CreateFeedHelper";
import { CreateFeedActions, CreateFeedActionTypes } from "Feed/CreateFeed/Redux/CreateFeed/CreateFeedActions";
import { ICreateFeedSettings, Visibility } from "Feed/CreateFeed/Redux/CreateFeed/CreateFeedContracts";
import { CreateFeedService } from "Feed/CreateFeed/Services/CreateFeedService";
import { IdentityDataService } from "Feed/CreateFeed/Services/IdentityDataService";
import { ICreateFeedData } from "Feed/CreateFeed/Types";
import * as LegacyActions from "Feed/Legacy/Actions/Actions";
import { IProjectData } from "Tfs/Platform/Page";
import { currentProject } from "Tfs/ReduxCommon/Modules/TfsPageData/TfsPageData";

export default function* rootSaga(): SagaIterator {
    yield effects.takeEvery(CreateFeedActionTypes.CreateFeed, createFeed);
}

export function* createFeed(
    action: ActionWithPayload<CreateFeedActionTypes.CreateFeed, { data: ICreateFeedData }>
): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const identityDataService: IdentityDataService = pageContext.getService("IFeedIdentityDataService");
    const createFeedService: CreateFeedService = pageContext.getService("ICreateFeedService");
    const webPageDataService: IFeedWebPageDataService = pageContext.getService("IFeedWebPageDataService");

    try {
        yield effects.put(PerfTrackerActions.startPerformanceTracking(PerfScenarios.CreateProjectScopedFeed));

        yield effects.put(CreateFeedActions.saveStarted());
        yield effects.call(announce, Resources.CreateFeed_StartAnnouncement);

        // Construct ICreateFeedSettings
        const createFeedData = action.payload.data;

        const upstreamSources: UpstreamSource[] | undefined = createFeedData.upstreamSources
            ? // TODO: this call should eventually be calling a common selector that will get pieces from WebPageData
              webPageDataService.getDefaultPublicUpstreamSources()
            : undefined;
        const visibility = createFeedData.visibility;

        const feedSettings: ICreateFeedSettings = {
            identityGroups: [],
            feedName: createFeedData.feedName,
            visibility,
            upstreamSources
        };

        if (createFeedData.isProjectScoped) {
            const project: IProjectData = (yield effects.select(currentProject)!) as any;
            const identityGroups: Identity[] = (yield effects.call(
                [identityDataService, identityDataService.listGroups],
                project.id
            )) as any;

            feedSettings.projectId = project.id;
            feedSettings.identityGroups = identityGroups;
        }

        // Create feed
        const constructedFeed: Feed = (yield effects.call(constructNewFeed, pageContext, feedSettings)) as any;
        const createdFeed: Feed = (yield effects.call(
            [createFeedService, createFeedService.createFeedAsync],
            constructedFeed
        )) as any;

        // Update permissions
        yield* updateViewPermissions(pageContext, createdFeed, visibility);
        createdFeed.permissions = (yield effects.call(
            [createFeedService, createFeedService.getFeedPermissionsAsync],
            createdFeed
        )) as any;

        // Add new feed to the store
        // TODO: Remove Legacy action call below once feed list is coming from the new store
        LegacyActions.NewFeedCreated.invoke({ createdFeed });
        yield effects.put(CreateFeedActions.saveCompleted());

        // accessibility announcement
        yield effects.call(
            announce,
            Utils_String.format(Resources.CreateFeed_SuccessAnnouncement, createdFeed.name),
            true
        );

        yield effects.put(FeedHubActions.feedAdded(createdFeed));
        yield effects.put(FeedHubActions.feedSelected(createdFeed.id));

        yield effects.put(FeedHubActions.hideCreateFeed());
    } catch (error) {
        let errorMessage = error.message;
        // Adding link for feed recycle bin error message
        if (error.serverError && error.serverError.typeKey == "FeedNotReleasedException") {
            errorMessage += " " + Resources.CreateFeed_FailedLinkFeedRecycleBin;
        }
        yield effects.put(CreateFeedActions.saveFailed(errorMessage));
        yield effects.call(announce, Resources.CreateFeed_ErrorAnnouncement + errorMessage, true);
    } finally {
        yield effects.put(PerfTrackerActions.endPerformanceTracking(PerfScenarios.CreateProjectScopedFeed));
    }
}

function* updateViewPermissions(pageContext: IVssPageContext, newFeed: Feed, visibility: string): SagaIterator {
    if (visibility === Visibility.private) {
        return newFeed;
    }

    const createFeedService: CreateFeedService = pageContext.getService<CreateFeedService>("ICreateFeedService");
    const webPageDataService = pageContext.getService<IFeedWebPageDataService>("IFeedWebPageDataService");

    // Case: add everyone group to the default view
    if (visibility === Visibility.public) {
        const feedPermissions: FeedPermission[] = [
            ({
                // TODO: this call should eventually be calling a common selector that will get pieces from WebPageData
                identityDescriptor: webPageDataService.getEveryoneGroup(),
                identityId: undefined,
                role: FeedRole.Reader
            } as unknown) as FeedPermission
        ];

        yield effects.call(
            [createFeedService, createFeedService.setFeedViewPermissionsAsync],
            newFeed,
            newFeed.defaultViewId,
            feedPermissions
        );
    }

    // Case: Update view visibility
    if (visibility === Visibility.collection || visibility === Visibility.aadTenant) {
        // TODO in a follow-up PR:
        // Check for FeedVisibility.project and update views to have project visibility if chosen in settings.
        // This needs some work in the Feed service.

        const visibilityEnum =
            visibility === Visibility.aadTenant
                ? FeedVisibility.AadTenant
                : visibility === Visibility.collection
                ? FeedVisibility.Collection
                : FeedVisibility.Private;
        const viewPatch: FeedView = { visibility: visibilityEnum } as FeedView;

        yield effects.call(
            [createFeedService, createFeedService.updateFeedViewAsync],
            newFeed,
            viewPatch,
            newFeed.defaultViewId
        );
    }
}
